/**
 * Authentication Context
 * Provides auth state and methods throughout the app
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { authApi } from '../lib/api/auth'
import { User, Admin } from '../lib/api/types'
import { UserRole } from '../types/auth'
import { 
  getStoredToken, 
  setStoredToken, 
  getStoredRole, 
  setStoredRole, 
  clearAuthData,
  isTokenExpiringSoon 
} from '../utils/token'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: User | null
  admin: Admin | null
  role: UserRole | null
  isLoading: boolean
  isAuthenticated: boolean
  signIn: (role: UserRole, email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  refreshToken: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [admin, setAdmin] = useState<Admin | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  /**
   * Load user profile from API
   */
  const loadProfile = useCallback(async (currentRole: UserRole) => {
    try {
      const response = await authApi.getCurrentProfile(currentRole)
      
      if (currentRole === 'admin') {
        const adminResponse = response as { admin: Admin }
        setAdmin(adminResponse.admin)
        setUser(null)
        localStorage.setItem('user_email', adminResponse.admin.email)
        localStorage.setItem('user_id', adminResponse.admin.id.toString())
      } else {
        const userResponse = response as { user: User }
        setUser(userResponse.user)
        setAdmin(null)
        localStorage.setItem('user_email', userResponse.user.email)
        localStorage.setItem('user_id', userResponse.user.id.toString())
      }
    } catch (error: any) {
      console.error('Failed to load profile:', error)
      // Clear invalid token
      clearAuthData()
      setUser(null)
      setAdmin(null)
      setRole(null)
    }
  }, [])

  /**
   * Refresh profile from API
   */
  const refreshProfile = useCallback(async () => {
    const currentRole = getStoredRole()
    if (!currentRole) return
    
    try {
      await loadProfile(currentRole)
    } catch (error) {
      console.error('Failed to refresh profile:', error)
    }
  }, [loadProfile])

  /**
   * Check and refresh token if needed
   */
  const refreshToken = useCallback(async () => {
    const token = getStoredToken()
    const currentRole = getStoredRole()
    
    if (!token || !currentRole) return
    
    // If token is expiring soon, we would refresh it here
    // For now, JWT tokens are stateless, so we just check validity
    if (isTokenExpiringSoon(token, 10)) {
      // Token is expiring soon, but we can't refresh without credentials
      // The user will need to sign in again when token expires
      console.warn('Token is expiring soon')
    }
  }, [])

  /**
   * Sign in with role, email, and password
   */
  const signIn = useCallback(async (userRole: UserRole, email: string, password: string) => {
    try {
      const response = await authApi.signIn(userRole, email, password)
      
      // Store token and role
      let token: string
      if (userRole === 'admin') {
        const adminResponse = response as { token: string; admin: Admin }
        token = adminResponse.token
        setStoredToken(token)
        setStoredRole(userRole)
        setAdmin(adminResponse.admin)
        setUser(null)
        localStorage.setItem('user_email', adminResponse.admin.email)
        localStorage.setItem('user_id', adminResponse.admin.id.toString())
      } else {
        const userResponse = response as { token: string; user: User }
        token = userResponse.token
        setStoredToken(token)
        setStoredRole(userRole)
        setUser(userResponse.user)
        setAdmin(null)
        localStorage.setItem('user_email', userResponse.user.email)
        localStorage.setItem('user_id', userResponse.user.id.toString())
      }
      
      setRole(userRole)
      toast.success('Logged in successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Login failed')
      throw error
    }
  }, [])

  /**
   * Sign out current user/admin
   */
  const signOut = useCallback(async () => {
    const currentRole = getStoredRole()
    
    try {
      if (currentRole) {
        await authApi.signOut(currentRole)
      }
    } catch (error) {
      console.error('Sign out error:', error)
      // Even if API call fails, clear local data
    } finally {
      clearAuthData()
      setUser(null)
      setAdmin(null)
      setRole(null)
      toast.success('Logged out successfully!')
    }
  }, [])

  // Load user profile on mount if token exists
  useEffect(() => {
    const token = getStoredToken()
    const storedRole = getStoredRole()
    
    if (token && storedRole) {
      setRole(storedRole)
      loadProfile(storedRole).finally(() => {
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [loadProfile])

  // Set up token refresh interval (check every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      refreshToken()
    }, 5 * 60 * 1000) // 5 minutes

    return () => clearInterval(interval)
  }, [refreshToken])

  const value: AuthContextType = {
    user,
    admin,
    role,
    isLoading,
    isAuthenticated: !!user || !!admin,
    signIn,
    signOut,
    refreshProfile,
    refreshToken,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

